import React, { useState, useContext, ReactElement } from 'react'
import {
  HeadingText,
  Container,
  Row,
  Col,
  HideIcon,
  CrowdfundIcon,
  GoalsIcon,
  ShareMessengerIcon,
  CopyToClipboardIcon,
  BodyText,
  Link,
  HorizontalScroll,
  CaptionText,
} from '@paypalcorp/pp-react'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'
import * as styles from './intro.css'
import useWindowSize from '../../utils/useWindowSize'
import serverData from '../../utils/server-data'
import { getCurrencyElements, getImageSrcSet } from './IntroHelpers'
import { PAYPAL_OBJECTS_DIRECTORY } from '../../constants'
import { ImageConfiguration } from '../../../../typings'
import { tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'

type IntroPage = {
  id: string
  images: ImageConfiguration
  title: string
  firstIcon: ReactElement
  firstSubtitle: string
  secondIcon: ReactElement
  secondSubtitle: string
  showPrivacyLink?: boolean
}

export const IntroContent = ({
  goToPrivacyScreen,
  pageVariant,
}: {
  goToPrivacyScreen: () => void
  pageVariant: string
}) => {
  const { isSmallScreen, isSmallerScreen } = useWindowSize()
  const currentUserCountryCode = useContext(WorldReadyContext).region
  const [currentPage, setCurrentPage] = useState(1)
  const handlePageChange = (index: number) => {
    tracking(
      Events.p2p_pools_intro_screen_swipe({
        source: serverData.trackingParameters.externalSource,
        page_variant: pageVariant,
      }),
    )
    setCurrentPage(index)
  }
  const BulletText = isSmallerScreen ? CaptionText : BodyText
  const renderIntroPage = (item: IntroPage) => {
    return (
      <div className={styles.introContentContainer} data-testid={item.id}>
        <Container fluid className={styles.container} form={true}>
          <Row row={{ alignContent: 'center' }} noGutters={true}>
            <Col className={styles.mainImageContainer}>
              <picture>
                <img
                  className={styles.carouselImage}
                  srcSet={getImageSrcSet(PAYPAL_OBJECTS_DIRECTORY, item.images)}
                  alt=""
                  aria-hidden="true"
                />
              </picture>
            </Col>
          </Row>
          <Row noGutters={true}>
            <Col>
              <HeadingText
                as="h1"
                size="sm"
                type="strong"
                className={styles.heading}
              >
                <Message id={item.title} />
              </HeadingText>
            </Col>
          </Row>
          <Row noGutters={true}>
            <Col xs={2}>
              <div className={styles.introIcons}>{item.firstIcon}</div>
            </Col>
            <Col xs={10}>
              <BulletText as="p" className={styles.introText}>
                <Message id={item.firstSubtitle} />
              </BulletText>
            </Col>
          </Row>
          <Row noGutters={true}>
            <Col xs={2}>
              <div className={styles.introIcons}>{item.secondIcon}</div>
            </Col>
            <Col xs={10}>
              <BulletText as="p" className={styles.introText}>
                <Message id={item.secondSubtitle} />
                {item.showPrivacyLink && (
                  <>
                    {' '}
                    <Link
                      tabIndex="0"
                      data-testid="learnMoreButton"
                      onClick={goToPrivacyScreen}
                    >
                      <Message id="create.intro.learnMoreButton" />
                    </Link>
                  </>
                )}
              </BulletText>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  const { CurrencyComponent, currencyDataTestId } = getCurrencyElements(
    currentUserCountryCode,
  )

  const introPages: Array<IntroPage> = [
    {
      id: 'firstPanelIntroContent',
      images: serverData?.ui?.introCarouselImagesByCountry?.firstImage || {},
      title: 'create.intro.title',
      firstIcon: (
        <CrowdfundIcon size={isSmallScreen ? 'sm' : 'md'} aria-hidden="true" />
      ),
      firstSubtitle: 'create.intro.trackItem',
      secondIcon: (
        <HideIcon size={isSmallScreen ? 'sm' : 'md'} aria-hidden="true" />
      ),
      secondSubtitle: 'create.intro.privacyItem',
      showPrivacyLink: true,
    },
    {
      id: 'secondPanelIntroContent',
      images: serverData?.ui?.introCarouselImagesByCountry?.secondImage || {},
      title: 'create.intro.secondTitle',
      firstIcon: (
        <GoalsIcon size={isSmallScreen ? 'sm' : 'md'} aria-hidden="true" />
      ),
      firstSubtitle: 'create.intro.meetingGoal',
      secondIcon: (
        <CurrencyComponent
          size={isSmallScreen ? 'sm' : 'md'}
          data-testid={currencyDataTestId}
          aria-hidden="true"
        />
      ),
      secondSubtitle: 'create.intro.chipInGuest',
    },
    {
      id: 'thirdPanelIntroContent',
      images: serverData?.ui?.introCarouselImagesByCountry?.thirdImage || {},
      title: 'create.intro.thirdTitle',
      firstIcon: (
        <ShareMessengerIcon
          size={isSmallScreen ? 'sm' : 'md'}
          aria-hidden="true"
        />
      ),
      firstSubtitle: 'create.intro.sharePool',
      secondIcon: (
        <CopyToClipboardIcon
          size={isSmallScreen ? 'sm' : 'md'}
          aria-hidden="true"
        />
      ),
      secondSubtitle: 'create.intro.copyPaste',
    },
  ]

  return (
    <HorizontalScroll
      items={introPages}
      renderItem={renderIntroPage}
      numOfVisibleItems={1}
      currentPage={currentPage}
      onPageChange={handlePageChange}
    />
  )
}
